import { useEffect } from "react";
import { STEP_NAMES } from "@/modules/Quote/constants.js";
import { CheckIcon, ShieldIcon } from "@/shared/components/Icon.jsx";
import { ToggleLeft } from "@/shared/components/desktop/HubIcons.jsx";
import { ProgressBar } from "@/modules/Quote/components/ProgressBar.jsx";
import { fetchSpecialityByCode } from "@/modules/Quote/api/specialityApi.js";
import { DEFAULT_SPECIALITY_CODE } from "@/shared/services/config.js";
import { useStore } from "@/shared/store/useStore.js";
import quoteFormStore from "@/modules/Quote/store/quoteFormStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import signupStore from "@/modules/Auth/store/signupStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import { useSpeciality } from "@/hooks/useSpeciality.js";
import { useQuoteSnapshot } from "@/modules/Quote/utils/useQuoteSnapshot.js";
import {
  TEAL_50, TEAL_100, TEAL_600, TEAL_700, TEAL_800, TEAL_900,
  SLATE_100, SLATE_200, SLATE_400, SLATE_500, SLATE_600, SLATE_800, SLATE_900,
  EMERALD_100, FONT_SANS, FONT_MONO,
} from "@/shared/components/desktop/hubTheme.js";

// Desktop hub redesign palette. The rail is desktop-only (hidden < 1024px
// via `.desktop-rail`), so these hub teal/slate values never reach the
// mobile UI. They replace the former runtime BRAND tokens here on purpose:
// the hub look does follow the theme toggle via the hub-brand-* ramp.

// Desktop-only side rail. Always rendered by FlowLayout; hidden via CSS
// below 1024px (see src/responsive.css `.desktop-rail` default `display: none`).
// View-only: no clicks, no state, no fetches. Reads from stores and degrades
// gracefully when individual fields are empty.
//
// Per spec section 3, the rail surfaces three distinct OOUX objects as
// stacked cards: Quote summary, Stepper, and Need help. The Insured
// object gets its own card when authenticated.
//
// The rail also renders the ProgressBar and footer line at its top
// (desktop only) so the right column reads as one vertical stack per
// the design mock. The mobile-position copies in FlowLayout are hidden
// on desktop via src/responsive.css.
export default function QuoteSnapshotRail() {
  const step    = useStore(submissionStore, (s) => s.step);
  const effDate = useStore(quoteFormStore, (s) => s.effDate);
  const regFirstName = useStore(signupStore, (s) => s.regFirstName);
  const regLastName  = useStore(signupStore, (s) => s.regLastName);
  const regFullName  = [regFirstName, regLastName].filter(Boolean).join(" ");
  const insuredProfile = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);
  const specialityMaster = useSpeciality();
  const {
    snapshotState, snapshotZip, snapshotBasePremium, snapshotLimits,
    snapshotPolicyLine, snapshotExpMdY, headerUserInitials,
  } = useQuoteSnapshot();

  // The rail shows the "Trade" row on every flow page, but the speciality
  // master (which supplies it) is only auto-fetched on the landing paths and
  // lives in an in-memory store. So any page reached without passing through
  // `/` — or any reload on a deeper page — leaves it null and the Trade row
  // collapses. Since this rail is the row's only consumer and renders wherever
  // it's needed, we fetch the master here when it's missing. The API helper is
  // module-cached + inflight-deduped, so this is at most one GET per tab.
  useEffect(() => {
    if (specialityMaster) return;
    fetchSpecialityByCode(DEFAULT_SPECIALITY_CODE).catch(() => {
      // Best-effort: a failed lookup just leaves the Trade row collapsed,
      // exactly as before this fetch existed.
    });
  }, [specialityMaster]);

  // specialityMaster comes from GET /api/weborder/v1/auth/speciality/:code.
  // Probed the live dev endpoint (SP_203 → {id, title, abbreviation,
  // specialitiesCategory, description, speiclaityRandomCode}) — the
  // human-readable trade name lives in `title`. We fall through to
  // `specialityName` / `name` / `displayName` so a backend shape change
  // in either direction degrades to a populated row instead of an empty
  // one. We deliberately do NOT fall through to `description`: it's a
  // long-form marketing string that would truncate to an ugly ellipsis
  // in the 360px rail — better to collapse the row than show "Plumbing
  // contractors install and maint…". Empty string collapses the row.
  const tradeLabel = (
    specialityMaster?.title
    || specialityMaster?.specialityName
    || specialityMaster?.name
    || specialityMaster?.displayName
    || ""
  ).toString().trim();
  const stateLabel = snapshotState && snapshotState !== "Your state"
    ? String(snapshotState).trim()
    : "";
  const zipLabel = (snapshotZip || "").toString().trim();
  const effLabel = (effDate || "").toString().trim();
  const expLabel = (snapshotExpMdY || "").toString().trim();
  const limitsLabel = snapshotLimits && snapshotLimits !== "—" ? snapshotLimits : "";
  const policyLine = (snapshotPolicyLine || "").toString().trim();

  const premiumNum = Number(snapshotBasePremium);
  const hasPremium = Number.isFinite(premiumNum) && premiumNum > 0;

  // Top line — name from /insured/session. Falls back to a full name
  // (e.g. just after signup, before the session DTO has a name) and then
  // to the email so the card never collapses.
  const sessionName = (insuredProfile?.name || "").toString().trim();
  const sessionEmail = (insuredProfile?.username || "").toString().trim();
  const sessionFullName = (insuredProfile?.firstname && insuredProfile?.lastname)
    ? `${insuredProfile.firstname} ${insuredProfile.lastname}`.trim()
    : "";
  const insuredName = sessionName
    || (regFullName || "").trim()
    || sessionFullName
    || sessionEmail;
  const insuredEmail = sessionEmail;

  // Step 0 with effectively no quote data yet → show a friendly intro.
  const hasAnyQuoteData = Boolean(
    tradeLabel || stateLabel || zipLabel || effLabel || limitsLabel || hasPremium
  );
  const showIntro = step === 0 && !hasAnyQuoteData;

  const showInsuredCard = isAuthenticated && (insuredName || headerUserInitials);

  return (
    <aside className="desktop-rail" aria-label="Quote snapshot">
      {/* Desktop-only progress bar. Mirrors the mobile rendering in
          FlowLayout (hidden via CSS on desktop) so the rail can present
          it at the top of the right column per the design mock. */}
      <div className="rail-progress"><ProgressBar /></div>
      <div style={wrap}>
        {/* Soft-quote badge header (hub design): "Classic" pill + step
            counter mirror. Read-only — counter follows the wizard step. */}
        <div style={softQuoteHeader}>
          <span style={classicPill}>
            <ToggleLeftMini /> Classic
          </span>
          <div style={{ textAlign: "right" }}>
            <span style={softQuoteCounter}>Soft Quote · Step {step + 1} of {STEP_NAMES.length}</span>
            <div style={softQuoteStepName}>{STEP_NAMES[step] || ""}</div>
          </div>
        </div>

        {/* Card 1 — Quote object */}
        <section style={card}>
          <div style={cardTitle}>Your active quote</div>
          <div style={cardDivider} />
          {showIntro ? (
            <div style={introText}>
              We'll build your quote here as you go.
            </div>
          ) : (
            <>
              {/* Trade/State/ZIP/Policy render as a headline block ("Physician
                  Assistant" over "California · ZIP 92692" over "Claims-made ·
                  Inclusive…") instead of label/value rows, per design. Each
                  line collapses when its data is missing, same as SnapshotRow. */}
              {(tradeLabel || stateLabel || zipLabel || policyLine) ? (
                <div style={{ marginBottom: 10 }}>
                  {tradeLabel ? <div style={tradeHeading}>{tradeLabel}</div> : null}
                  {(stateLabel || zipLabel) ? (
                    <div style={tradeSubline}>
                      {[stateFullName(stateLabel), zipLabel ? `ZIP ${zipLabel}` : ""].filter(Boolean).join(" · ")}
                    </div>
                  ) : null}
                  {policyLine ? <div style={tradeSubline}>{policyLine}</div> : null}
                </div>
              ) : null}
              <SnapshotRow label="Effective" value={effLabel} />
              <SnapshotRow label="Expires" value={expLabel} />
              <SnapshotRow label="Limits" value={limitsLabel} />
            </>
          )}
        </section>

        {/* Card 2 — Stepper (read-only mirror of the top-bar "Step X of Y") */}
        <section style={card}>
          <div style={cardTitle}>Application Steps</div>
          <div style={cardDivider} />
          <ol style={stepList}>
            {STEP_NAMES.map((name, i) => {
              const status = i < step ? "done" : i === step ? "current" : "pending";
              return (
                <li
                  key={name}
                  style={stepItem}
                  aria-current={status === "current" ? "step" : undefined}
                >
                  <span style={stepBullet(status)} aria-hidden="true">
                    {status === "done" ? (
                      <CheckIcon size={12} />
                    ) : status === "current" ? (
                      <span style={currentDot} />
                    ) : (
                      <span style={pendingDot} />
                    )}
                  </span>
                  <span style={stepText(status)} title={name}>{name}</span>
                </li>
              );
            })}
          </ol>
          {/* Goal chip — the *reward* at the end of the wizard, not a
              real step. Visually distinct from the list items (icon +
              filled background) so the brain doesn't habituate to it as
              "another chore". Brightens as the user gets close (goal-
              gradient effect): muted at the start of the funnel, full
              brand color on the Review step, and a soft pulse on the
              Payment step. Rendered outside the <ol> so it doesn't
              inherit list semantics. Not driven by STEP_NAMES — that
              array controls the topbar progress and adding a 10th entry
              would inflate "Step X of N" everywhere. */}
          <div style={goalChip(step)} aria-label="You're covered (goal)">
            <span style={goalChipIcon(step)} aria-hidden="true">
              <ShieldIcon size={14} />
            </span>
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
              <span style={goalChipLabel(step)}>You're covered</span>
              <span style={goalChipSub(step)}>{goalSubLabel(step)}</span>
            </div>
          </div>
        </section>

        {/* Card 3 — Insured object (only when authenticated) */}
        {showInsuredCard ? (
          <section style={card}>
            <div style={cardTitle}>Signed in</div>
            <div style={cardDivider} />
            <div style={insuredRow}>
              <div style={avatar}>{headerUserInitials}</div>
              <div style={{ minWidth: 0 }}>
                <div style={insuredNameStyle}>{insuredName || "Signed in"}</div>
                {insuredEmail ? (
                  <div style={insuredSub} title={insuredEmail}>{insuredEmail}</div>
                ) : null}
              </div>
            </div>
          </section>
        ) : null}

        {/* Card 4 — Need help. Phone + email, per spec section 4.3.
            The support email is the canonical one used throughout
            src/layout/Modals.jsx (About modal contact block, Terms §3
            and §6 cancellation contact, Privacy §… contact). */}
        <section style={card}>
          <div style={cardTitle}>Need help?</div>
          <div style={cardDivider} />
          <div style={helpRow}>
            <span style={helpLabel}>Call</span>
            <a href="tel:+18889663881" style={helpValue}>(888) 966-3881</a>
          </div>
          <div style={helpRow}>
            <span style={helpLabel}>Email</span>
            <a
              href="mailto:support@selectfirstinsurance.com"
              style={helpValue}
              title="support@selectfirstinsurance.com"
            >
              support@selectfirstinsurance.com
            </a>
          </div>
        </section>
      </div>
    </aside>
  );
}

// Two-letter code → full state name for the headline subline ("CA" →
// "California"). Covers the 20 states the carrier writes in (MAP_STATES in
// MedMalGuardLanding) — an unknown code falls through unchanged.
const STATE_NAMES = {
  AZ: "Arizona", CA: "California", CO: "Colorado", CT: "Connecticut",
  DE: "Delaware", FL: "Florida", GA: "Georgia", HI: "Hawaii",
  IL: "Illinois", MD: "Maryland", MI: "Michigan", NC: "North Carolina",
  NJ: "New Jersey", NY: "New York", OH: "Ohio", PA: "Pennsylvania",
  SC: "South Carolina", TN: "Tennessee", TX: "Texas", WA: "Washington",
};

function stateFullName(code) {
  if (!code) return "";
  return STATE_NAMES[String(code).trim().toUpperCase()] || String(code).trim();
}

function SnapshotRow({ label, value }) {
  if (value == null || String(value).trim() === "") return null;
  return (
    <div style={rowStyle}>
      <span style={rowLabel}>{label}</span>
      <span style={rowValue}>{String(value)}</span>
    </div>
  );
}

function ToggleLeftMini() {
  return <span style={{ display: "inline-flex", marginRight: 2 }}><ToggleLeft size={12} color={TEAL_700} /></span>;
}

// --- soft-quote header styles (hub design) ------------------------------
const softQuoteHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: 12,
  borderBottom: `1px solid ${SLATE_100}`,
};

const classicPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.1em",
  color: TEAL_800,
  textTransform: "uppercase",
  background: TEAL_50,
  padding: "3px 8px",
  borderRadius: 4,
  border: `1px solid ${TEAL_100}`,
};

const softQuoteCounter = {
  fontFamily: FONT_MONO,
  fontSize: 11,
  color: SLATE_400,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const softQuoteStepName = {
  fontSize: 12,
  fontWeight: 700,
  color: TEAL_800,
  letterSpacing: "-0.01em",
  marginTop: 2,
};

// --- styles ---------------------------------------------------------------

const wrap = {
  position: "sticky",
  top: 32,
  // Cap height so a tall rail (step list + insured card + help card) on
  // a short form column (e.g. step 0 with two fields) self-scrolls
  // instead of clipping at the viewport bottom. 64px = top offset (32)
  // + a symmetric breathing gap at the bottom.
  maxHeight: "calc(100vh - 64px)",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  fontFamily: FONT_SANS,
};

const card = {
  background: "#fff",
  border: `1px solid ${SLATE_200}`,
  borderRadius: 16,
  padding: "20px 22px",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const cardTitle = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: SLATE_400,
  fontFamily: FONT_MONO,
};

const cardDivider = {
  height: 1,
  background: SLATE_100,
  margin: "12px -22px 14px",
};

const introText = {
  fontSize: 13,
  color: SLATE_500,
  lineHeight: 1.5,
};

const tradeHeading = {
  fontSize: 18,
  fontWeight: 800,
  color: SLATE_900,
  letterSpacing: "-0.01em",
  lineHeight: 1.25,
};

const tradeSubline = {
  fontSize: 12,
  fontWeight: 500,
  color: SLATE_500,
  marginTop: 3,
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  fontSize: 12,
  padding: "5px 0",
  color: SLATE_800,
};

const rowLabel = {
  color: SLATE_500,
  fontWeight: 500,
  flexShrink: 0,
};

const rowValue = {
  color: SLATE_800,
  textAlign: "right",
  fontWeight: 700,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const stepList = {
  listStyle: "none",
  margin: 0,
  padding: "0 0 0 4px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const stepItem = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  fontSize: 12,
  lineHeight: 1,
};

const stepBullet = (status) => ({
  width: 16,
  height: 16,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  background: status === "done" ? EMERALD_100 : "transparent",
  color: status === "done" ? "#047857" : status === "current" ? TEAL_800 : "#cbd5e1",
  flexShrink: 0,
});

const currentDot = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: TEAL_800,
  display: "inline-block",
};

const pendingDot = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  border: "1px solid #cbd5e1",
  background: SLATE_100,
  display: "inline-block",
  boxSizing: "border-box",
};

const stepText = (status) => ({
  color: status === "pending" ? SLATE_400 : status === "current" ? TEAL_900 : SLATE_500,
  fontWeight: status === "current" ? 700 : 400,
  textDecoration: status === "done" ? "line-through" : "none",
  textDecorationColor: status === "done" ? SLATE_200 : undefined,
  letterSpacing: "0.01em",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const insuredRow = {
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const avatar = {
  width: 32,
  height: 32,
  borderRadius: "50%",
  background: TEAL_50,
  color: TEAL_800,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  fontWeight: 700,
  flexShrink: 0,
};

const insuredNameStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: SLATE_800,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const insuredSub = {
  fontSize: 11,
  color: SLATE_400,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const helpRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  fontSize: 12,
  padding: "5px 0",
  fontWeight: 500,
  color: SLATE_600,
};

const helpLabel = { color: SLATE_500, flexShrink: 0 };

const helpValue = {
  color: TEAL_800,
  textDecoration: "none",
  fontWeight: 700,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

// --- Goal chip ----------------------------------------------------------
// Three intensities based on how close `step` is to the finish:
//   far       (step <= 5, 3+ to go):        muted; present but quiet
//   near      (step 6-7, Review/Payment):   full brand color, no pulse
//   imminent  (step >= 8, Binder):          full color + soft pulse
// The intensity function is shared by all chip styles so they shift in
// lockstep — change here, not in five places.
function chipIntensity(step) {
  if (step >= 8) return "imminent";
  if (step >= 6) return "near";
  return "far";
}

function goalSubLabel(step) {
  const remaining = Math.max(0, 9 - step);
  if (remaining === 0) return "Almost there";
  if (remaining === 1) return "1 step to go to lock binder";
  return `${remaining} steps to go to lock binder`;
}

const goalChip = (step) => {
  const intensity = chipIntensity(step);
  return {
    marginTop: 24,
    paddingTop: 16,
    borderTop: `1px solid ${SLATE_100}`,
    display: "flex",
    alignItems: "center",
    gap: 12,
    transition: "background 0.3s, border-color 0.3s",
    animation: intensity === "imminent" ? "q2bGoalPulse 1.8s ease-in-out infinite" : "none",
    // inner chip styling lives via the wrapper below — keep the top
    // divider look matching the hub's "You're covered" footer block.
    background: "transparent",
  };
};

const goalChipIcon = (step) => {
  const intensity = chipIntensity(step);
  return {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: intensity === "far" ? "#e2e8f0" : TEAL_600,
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
    transition: "background 0.3s, color 0.3s",
  };
};

const goalChipLabel = (step) => {
  const intensity = chipIntensity(step);
  return {
    fontSize: 12,
    fontWeight: 700,
    color: intensity === "far" ? SLATE_500 : TEAL_900,
    transition: "color 0.3s",
  };
};

const goalChipSub = (step) => {
  const intensity = chipIntensity(step);
  return {
    fontSize: 11,
    color: intensity === "far" ? SLATE_400 : TEAL_700,
    fontWeight: 500,
    transition: "color 0.3s",
  };
};
